import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { TitleService } from '@app/services/title.service';
import { RunnerData, showRunnerDialog } from '@app/shared/components/argument-card/runner-details-dialog/runner-details';
import { MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup, FormArray, MaxValidator, Validators } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { TourGuideService } from '@app/services/tour-guide.service';
import { TourGuideContent } from '@app/util/tour-guide-content.util';
import dummyData from '@app/assets/api-dummy-data.json';
import { CommonFunctionalitiesService } from '@app/services/common-functionalities.service';
import { DynamicDropdownService } from '@app/services/dynamic-dropdown.service';
import { Messages } from '@app/src/util/messages';
import { Keys } from '@app/src/util/labels.util';
import { AuthService } from '@app/services/auth/auth.service';

@Component({
  selector: 'app-input-and-arguments',
  templateUrl: './input-and-arguments.component.html',
  styleUrls: ['./input-and-arguments.component.scss']
})
export class InputAndArgumentsComponent implements OnInit {

  // Variables
  displayedColumns: string[] = ['key', 'description', 'payload', 'default'];
  runnerObj: any = {};
  inputArgArray: any = [];  // Array to hold input arguments
  runnertext: string = '';
  inputArgForm: FormGroup = this.fb.group({
    inputGroupArray: this.fb.array([])
  }); // Form array to hold input arguments
  runnerLabels: any = [];
  defaultData: any = [];
  tourGuidIds = TourGuideContent;
  getNoData = Messages.no_data; //for getting no data available message
  readOnlyFields : string[] =["BUILDER_GMID"];
  editIcon = this.titleService.editIcon;

  // Input properties
  @Input('isEditable') isEditable: boolean; // Flag to check if the component is in edit mode
  @Input() mode: any;  // Mode of the component (read/edit)
  @Input() getData: any; // Data to be passed to the component
  /**Input variable when manual workflow is triggered */
  @Input() manualTrigger: boolean;
  @Input() manualTriggerRes: boolean;
  @Input() changeEventStatus: boolean = false;  // Flag to check if change event is triggered
  @Input() changedEventValue: number; // Value to be changed on event
  @Input() set list(data: any) {
    if (Array.isArray(data) && data.length > 0) {

      data = data?.map((el: any) => {
        if (["upload_url", "URL"].includes(el.key)) {
          el = { ...el, default: this.commonService.appendSubString(el.default), value: this.commonService.appendSubString(el.value) }
        }
        return el;
      })

      this.inputArgForm = this.fb.group({
        inputGroupArray: this.fb.array([])
      })
      let object_Arr: any = [];

      data?.forEach((input: any) => {

        if (![Keys.runnerDetails].includes(input.key)) {
          if (this.commonService.isJSONObject(input.default)) {
            object_Arr = Object.entries(JSON.parse(input?.value || input.default))?.map((el => {
               // Assign logged in GMID if the input params key is 'BUILDER_GMID' 
               if(this.readOnlyFields[0] === el[0]){
                el[1] = this.authService?.gmId || ''
              }
              
              return { key: el[0], default: el[1], required: input.required, description: el[0], value: el[1], readOnly: this.readOnlyFields.includes(el[0]) }
            }))
            object_Arr.forEach((el: any) => {
              this.pushFormControls(el)
            })
            return
          }
        }

        let idx = this.inputGroupArray.value.findIndex((el: any) => el?.key == Keys.runnerDetails)

        this.pushFormControls(input)
        if (input.key.toLowerCase() == Keys.runnerDetails) {
          let runObj = this.inputGroupArray.at(idx).value;

          // this.runnertext = `[${runObj.value?.type}][${runObj.value?.os}][${runObj.value?.label}]`
          this.inputGroupArray.at(idx).patchValue({ value: runObj.value?.label || runObj.value })
        }
      })

      this.defaultData = data;

      this.valueChanges();


      if (!this.isEditable) {
        this.inputArgArray = data;
        this.inputArgOutput.emit({ inputArgArray: this.inputArgArray, isArgValid: this.inputArgForm.valid })
      }

      if (this.isEditable) {
        this.inputArgOutput.emit({ isArgValid: this.inputArgForm.valid })
      }

      this.inputGroupArray.controls.forEach((el: any, index) => {
        if (typeof el.value.value === "string" && el.value.value?.includes("$") && !(el.value.value?.includes("${{"))) {
          el.get('isSelected').setValue(true);
        } else {
          el.get('isSelected').setValue(false);
        }
      });
    } else {
      this.inputArgForm = this.fb.group({
        inputGroupArray: this.fb.array([])
      })
    }
  }

  // Output properties
  @Output() inputArgOutput = new EventEmitter<any>(); // Event emitter to output input arguments

  /**
   * Constructor for InputAndArgumentsComponent
   * @param titleService  Title service for setting the page title
   * @param dialog MatDialog instance for opening dialogs
   * @param fb FormBuilder instance for creating reactive forms
   * @param tourGuideService Tour guide service for managing tours
   * @param commonService Common functionalities service
   * @param dpService Dynamic dropdown service
   * @param authService Authentication service for user management
   */
  constructor(private titleService: TitleService,
    public dialog: MatDialog, private fb: FormBuilder,
    private tourGuideService: TourGuideService,
    private commonService: CommonFunctionalitiesService,
    public dpService: DynamicDropdownService,
    private authService: AuthService) { }

  /**ngOnInit */
  ngOnInit(): void {
    if (this.tourGuideService.isStartTour) {
      this.runnerLabels = dummyData.configBuilder.workflow.labels;
      this.runnerObj = {
        type: 'Azure',
        os: 'Windows',
        label: 'optimus-com'
      }
      this.columnChanges();
      return;
    }
    this.columnChanges();
  }

  /**ngOnChanges */
  ngOnChanges(changes: SimpleChanges): void {
    this.disableForm();
    if (changes['changedEventValue'] && changes['changedEventValue'].currentValue != changes['changedEventValue'].previousValue) {
      this.changeValueOnEventChange()
    }
  }

  /**inputGroupArray */
  get inputGroupArray() {
    if (this.inputArgForm) {
      return this.inputArgForm.get("inputGroupArray") as FormArray;
    }
    return {} as FormArray;
  }

  /**
   * Pushes a new form control for the input object into the form array
   * @param input - Input object to be added to the form controls
   */
  pushFormControls(input: any) {
    // Use the value as provided in the input object (which may already be the default value)
    const displayValue = input.value !== null && input.value !== undefined ? input.value : input.default;
    input = { ...input, value: displayValue, isSelected: (typeof displayValue === "string" && displayValue?.includes("$")) ? true : false };
    this.inputGroupArray.push(this.initRows(input));
  }

  /**
   * Column changes based on manual trigger
   */
  columnChanges() {
    if (this.manualTrigger) {
      this.displayedColumns = ['key', 'description', 'default']
    }
    if (this.manualTriggerRes) {
      this.displayedColumns = ['key', 'default']
    }
  }

  /**
   * Emits the input argument array and validity status on value changes
   */
  valueChanges() {
    this.inputArgForm.valueChanges.pipe(debounceTime(200)).subscribe((val: any) => {
      this.inputArgOutput.emit({ inputArgArray: [...this.inputGroupArray.value], isArgValid: this.inputArgForm.valid })
    });
  }

  /**
   * Initializes a form group for each row in the input arguments
   * @param row - Row data to initialize the form group
   * @returns FormGroup for the input row
   */
  initRows(row: any) {
    return this.fb.group({
      type: [row?.type],
      default: [row?.default || ''],
      description: [row?.description],
      from: [row?.from],
      key: [row?.key],
      name: [row?.name],
      value: [row.value, row.required ? Validators.required : ''],
      isSelected: [row?.isSelected],
      required: [row?.required],
      readOnly:[row?.readOnly || false]
    });
  }

  /**edit button on runner details */
  edit(item: any, idx: number) {
    if (item.key === "runner_details") {

      this.runnerObj = item.value?.type ? item.value : this.runnerObj;
      showRunnerDialog(this.dialog, { data: this.runnerObj }).subscribe((runnerData: RunnerData) => {
        if (runnerData) {
          this.runnerObj = runnerData;
          this.inputGroupArray.at(idx).patchValue({ ...item, value: runnerData })
          this.runnertext = `[${runnerData?.type}][${runnerData?.os}][${runnerData?.label}]`
          this.inputArgOutput.emit({ inputArgArray: this.inputArgArray, isArgValid: this.inputArgForm.valid })
        }
      });
    }
  }

  /**checkboxChanged function is used to change the input box to multi level dropdown */
  checkboxChanged(row: any): void {
    if (!row.value.isSelected) {
      this.inputGroupArray.controls.forEach((el: any, index) => {
        if (el.value.key == row.value.key && this.changeEventStatus) {
          el.get('value').setValue('');
        } else if (el.value.key == row.value.key && !this.changeEventStatus) {
          el.get('value').setValue(el.value.default);
        }
      });
    } else {
      this.intializeValuesBeforeEvent(row)
    }
  }

  /**
   * Disables or enables the form based on the mode
   */
  disableForm() {
    if (this.mode === 'read') {
      this.inputArgForm.disable();
    }
    else if (this.mode === 'edit') {
      this.inputArgForm.enable()
    }
  }

  /**
   * Changes the value of input arguments on event change
   */
  changeValueOnEventChange() {
    if (this.changeEventStatus) {
      this.inputGroupArray.controls.forEach((el: any) => {
        if (el.value?.isSelected) {
          el.patchValue({ isSelected: false, value: '' });
        }
      });
    }
  }

  /**
   * Initializes the form controls for the input arguments
   * @param row - The row data to initialize the form controls
   */
  intializeValuesBeforeEvent(row: any) {
    this.inputGroupArray.controls.forEach((el: any, index) => {
      if (el.value.key == row.value.key && el.value.isSelected) {
        el.get('value').setValue('')
        if (this.getData) {
          this.getData.forEach((ele: any) => {
            if (el.value.key == ele.key && typeof (ele.value) == 'string' && ele.value.includes('$') && !this.changeEventStatus) {
              el.get('value').setValue(ele.value);
            }
          })
        }
      }
    });
  }
}
